import React, { useState, useEffect } from 'react';
import { apiGet, apiPut } from '../../utils/apiFetch';
import apiPath from '../../utils/apiPath';
import { useTranslation } from 'react-i18next';
import useToastContext from 'hooks/useToastContext';
import OInputField from 'components/reusable/OInputField';
import { useForm } from 'react-hook-form';
import OSelect from 'components/reusable/OSelect';

const EmployeeWageSetup = ({ employeeId, onClose }) => {
  const { t } = useTranslation();
  const notification = useToastContext();

  const { register, setValue, watch, getValues, setError, clearErrors, formState: { errors } } = useForm({
    defaultValues: {
      payType: 'hourly',
      hourlyRate: '0',
      annualSalary: '0',
      overtimeMultiplier: '0',
      overtimeRate: '0',
      doubleTimeMultiplier: '0',
      payFrequency: 'bi-weekly',
      workingDaysPerWeek: '0',
      regularHoursPerDay: '0',
      expectedOvertimeHoursPerPeriod: '0',
      expectedDoubleTimeHoursPerPeriod: '0',
      federalTax: '0',
      stateTax: '0',
      socialSecurity: '0',
      medicare: '0',
      healthInsurance: '0',
      dentalInsurance: '0',
      visionInsurance: '0',
      retirement401k: '0',
      effectiveDate: new Date().toISOString().split('T')[0]
    }
  });

  const [loading, setLoading] = useState(false);
  const [employeeName, setEmployeeName] = useState('');

  // ═══ Watch all form values ═══
  const payType = watch('payType');
  const hourlyRate = watch('hourlyRate');
  const annualSalary = watch('annualSalary');
  const overtimeMultiplier = watch('overtimeMultiplier');
  const overtimeRate = watch('overtimeRate');
  const doubleTimeMultiplier = watch('doubleTimeMultiplier');
  const payFrequency = watch('payFrequency');
  const workingDaysPerWeek = watch('workingDaysPerWeek');
  const regularHoursPerDay = watch('regularHoursPerDay');
  const expectedOvertimeHoursPerPeriod = watch('expectedOvertimeHoursPerPeriod');
  const expectedDoubleTimeHoursPerPeriod = watch('expectedDoubleTimeHoursPerPeriod');
  const federalTax = watch('federalTax');
  const stateTax = watch('stateTax');
  const socialSecurity = watch('socialSecurity');
  const medicare = watch('medicare');
  const healthInsurance = watch('healthInsurance');
  const dentalInsurance = watch('dentalInsurance');
  const visionInsurance = watch('visionInsurance');
  const retirement401k = watch('retirement401k');

  // ═══ Calculation states ═══
  const [regularPay, setRegularPay] = useState(0);
  const [overtimePay, setOvertimePay] = useState(0);
  const [doubleTimePay, setDoubleTimePay] = useState(0);
  const [grossPay, setGrossPay] = useState(0);
  const [estimatedTaxes, setEstimatedTaxes] = useState(0);
  const [totalDeductions, setTotalDeductions] = useState(0);
  const [netPay, setNetPay] = useState(0);

  useEffect(() => {
    const fetchWageData = async () => {
      try {
        if (!employeeId) return;
        const res = await apiGet(`${apiPath.accountantEmployeeWage}/${employeeId}`);
        if (res?.data?.success && res?.data?.results) {
          const data = res.data.results;
          const wageDetails = data.wageDetails || {};

          // Set form values (fallback to 0 where applicable)
          setValue('payType', wageDetails.payType || 'hourly');
          setValue('hourlyRate', String(wageDetails.hourlyRate ?? 0));
          setValue('annualSalary', String(wageDetails.annualSalary ?? 0));
          setValue('overtimeMultiplier', String(wageDetails.overtimeMultiplier ?? 0));
          setValue('overtimeRate', String(wageDetails.overtimeRate ?? 0));
          setValue('doubleTimeMultiplier', String(wageDetails.doubleTimeMultiplier ?? 0));
          setValue('payFrequency', wageDetails.payFrequency || 'bi-weekly');
          setValue('workingDaysPerWeek', String(wageDetails.workingDaysPerWeek ?? 0));
          setValue('regularHoursPerDay', String(wageDetails.regularHoursPerDay ?? 0));
          setValue('expectedOvertimeHoursPerPeriod', String(wageDetails.expectedOvertimeHoursPerPeriod ?? 0));
          setValue('expectedDoubleTimeHoursPerPeriod', String(wageDetails.expectedDoubleTimeHoursPerPeriod ?? 0));
          setValue('federalTax', String(wageDetails.taxWithholding?.federalTax ?? 0));
          setValue('stateTax', String(wageDetails.taxWithholding?.stateTax ?? 0));
          setValue('socialSecurity', String(wageDetails.taxWithholding?.socialSecurity ?? 0));
          setValue('medicare', String(wageDetails.taxWithholding?.medicare ?? 0));
          setValue('healthInsurance', String(wageDetails.deductions?.healthInsurance ?? 0));
          setValue('dentalInsurance', String(wageDetails.deductions?.dentalInsurance ?? 0));
          setValue('visionInsurance', String(wageDetails.deductions?.visionInsurance ?? 0));
          setValue('retirement401k', String(wageDetails.deductions?.retirement401k ?? 0));
          setValue('effectiveDate', wageDetails.effectiveDate ? new Date(wageDetails.effectiveDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]);

          setEmployeeName(data.name || '');
        }
      } catch (error) {
        console.error('Error fetching wage data:', error);
      }
    };
    if (employeeId) {
      fetchWageData();
    }
  }, [employeeId, setValue]);

  // ═══ Calculate estimated pay ═══
  useEffect(() => {
    calculateEstimatedPay();
  }, [
    payType, hourlyRate, annualSalary, overtimeMultiplier, overtimeRate,
    doubleTimeMultiplier, payFrequency, workingDaysPerWeek, regularHoursPerDay,
    expectedOvertimeHoursPerPeriod, expectedDoubleTimeHoursPerPeriod,
    federalTax, stateTax, socialSecurity, medicare,
    healthInsurance, dentalInsurance, visionInsurance, retirement401k
  ]);

  const calculateEstimatedPay = () => {
    const hRate = parseFloat(hourlyRate) || 0;
    const oMultiplier = parseFloat(overtimeMultiplier) || 0;
    const oRate = parseFloat(overtimeRate) || (hRate * oMultiplier);
    const dMultiplier = parseFloat(doubleTimeMultiplier) || 0;
    const dRate = hRate * dMultiplier;
    const weeklyDays = parseInt(workingDaysPerWeek) || 0;
    const regHours = parseFloat(regularHoursPerDay) || 0;
    const otHours = parseFloat(expectedOvertimeHoursPerPeriod) || 0;
    const dtHours = parseFloat(expectedDoubleTimeHoursPerPeriod) || 0;
    const aSalary = parseFloat(annualSalary) || 0;

    // Calculate work days in period based on working days per week
    const workDaysInPeriodMap = {
      weekly: weeklyDays,
      'bi-weekly': weeklyDays * 2,
      'semi-monthly': Math.round(weeklyDays * 2.17),
      monthly: Math.round(weeklyDays * 4.33)
    };

    const workDaysInPeriod = workDaysInPeriodMap[payFrequency] || weeklyDays;

    let regularPayAmount = 0;
    let overtimePayAmount = 0;
    let doubleTimePayAmount = 0;

    // Calculate based on pay type
    if (payType === 'hourly') {
      regularPayAmount = hRate * regHours * workDaysInPeriod;
      overtimePayAmount = oRate * otHours;
      doubleTimePayAmount = dRate * dtHours;
    } else if (payType === 'salary') {
      switch (payFrequency) {
        case 'weekly':
          regularPayAmount = aSalary / 52;
          break;
        case 'bi-weekly':
          regularPayAmount = aSalary / 26;
          break;
        case 'semi-monthly':
          regularPayAmount = aSalary / 24;
          break;
        case 'monthly':
          regularPayAmount = aSalary / 12;
          break;
        default:
          regularPayAmount = aSalary / 26;
      }
      overtimePayAmount = 0;
      doubleTimePayAmount = 0;
    } else if (payType === 'contractor') {
      regularPayAmount = hRate * regHours * workDaysInPeriod;
      overtimePayAmount = 0;
      doubleTimePayAmount = 0;
    }

    const grossPayAmount = regularPayAmount + overtimePayAmount + doubleTimePayAmount;

    let taxWithheldAmount = 0;
    if (payType !== 'contractor') {
      const fTax = parseFloat(federalTax) || 0;
      const sTax = parseFloat(stateTax) || 0;
      const sSecurity = parseFloat(socialSecurity) || 0;
      const mCare = parseFloat(medicare) || 0;

      const totalTaxRate = (fTax + sTax + sSecurity + mCare) / 100;
      taxWithheldAmount = grossPayAmount * totalTaxRate;
    }

    let totalDeductionsAmount = 0;
    if (payType !== 'contractor') {
      const hInsurance = parseFloat(healthInsurance) || 0;
      const dInsurance = parseFloat(dentalInsurance) || 0;
      const vInsurance = parseFloat(visionInsurance) || 0;
      const r401k = parseFloat(retirement401k) || 0;
      totalDeductionsAmount = hInsurance + dInsurance + vInsurance + r401k;
    }

    const netPayAmount = grossPayAmount - taxWithheldAmount - totalDeductionsAmount;

    setRegularPay(regularPayAmount);
    setOvertimePay(overtimePayAmount);
    setDoubleTimePay(doubleTimePayAmount);
    setGrossPay(grossPayAmount);
    setEstimatedTaxes(taxWithheldAmount);
    setTotalDeductions(totalDeductionsAmount);
    setNetPay(netPayAmount);
  };

  // ═══ Handle number input with validation ═══
  const handleNumberInput = (field, value) => {
    let cleanedValue = value.replace(/[^0-9.-]/g, '');

    if (cleanedValue === '-' || cleanedValue === '') {
      setValue(field, cleanedValue);
      return;
    }

    const numValue = parseFloat(cleanedValue);
    if (numValue < 0) {
      setError(field, {
        type: 'manual',
        message: 'Value cannot be negative'
      });
      setValue(field, '0');
      return;
    }

    clearErrors(field);
    setValue(field, cleanedValue);
  };

  // ═══ Auto-calculate overtime rate ═══
  useEffect(() => {
    if (payType === 'hourly') {
      const hRate = parseFloat(hourlyRate) || 0;
      const oMultiplier = parseFloat(overtimeMultiplier) || 0;
      if (hRate > 0 && oMultiplier > 0) {
        const calculatedOvertimeRate = hRate * oMultiplier;
        setValue('overtimeRate', String(calculatedOvertimeRate));
      }
    }
  }, [hourlyRate, overtimeMultiplier, payType, setValue]);

  const handleSave = async () => {
    try {
      setLoading(true);
      const values = getValues();

      const validateField = (field, fieldName, min = 0, max = Infinity) => {
        if (field === 'payType' || field === 'payFrequency') return true;
        const value = parseFloat(values[field]);
        if (isNaN(value)) return true;
        if (value < min) {
          notification.error(`${fieldName} cannot be less than ${min}`);
          return false;
        }
        if (value > max) {
          notification.error(`${fieldName} cannot be greater than ${max}`);
          return false;
        }
        return true;
      };

      if (values.payType === 'hourly') {
        if (!validateField('hourlyRate', 'Hourly rate', 0)) return;
        if (!validateField('overtimeMultiplier', 'Overtime multiplier', 0)) return;
        if (!validateField('regularHoursPerDay', 'Regular hours per day', 0, 24)) return;
        if (!validateField('workingDaysPerWeek', 'Working days per week', 0, 7)) return;
      }

      if (values.payType === 'salary') {
        if (!validateField('annualSalary', 'Annual salary', 0)) return;
      }

      if (!validateField('expectedOvertimeHoursPerPeriod', 'Expected overtime hours', 0)) return;
      if (!validateField('expectedDoubleTimeHoursPerPeriod', 'Expected double time hours', 0)) return;

      if (values.payType !== 'contractor') {
        if (!validateField('federalTax', 'Federal tax', 0, 100)) return;
        if (!validateField('stateTax', 'State tax', 0, 100)) return;
        if (!validateField('socialSecurity', 'Social Security', 0, 100)) return;
        if (!validateField('medicare', 'Medicare', 0, 100)) return;
        if (!validateField('healthInsurance', 'Health Insurance', 0)) return;
        if (!validateField('dentalInsurance', 'Dental Insurance', 0)) return;
        if (!validateField('visionInsurance', 'Vision Insurance', 0)) return;
        if (!validateField('retirement401k', '401(k)', 0)) return;
      }

      const payload = {
        wageDetails: {
          payType: values.payType || 'hourly',
          hourlyRate: parseFloat(values.hourlyRate) || 0,
          annualSalary: parseFloat(values.annualSalary) || 0,
          overtimeMultiplier: parseFloat(values.overtimeMultiplier) || 0,
          overtimeRate: parseFloat(values.overtimeRate) || 0,
          doubleTimeMultiplier: parseFloat(values.doubleTimeMultiplier) || 0,
          payFrequency: values.payFrequency || 'bi-weekly',
          workingDaysPerWeek: parseInt(values.workingDaysPerWeek) || 0,
          regularHoursPerDay: parseFloat(values.regularHoursPerDay) || 0,
          expectedOvertimeHoursPerPeriod: parseFloat(values.expectedOvertimeHoursPerPeriod) || 0,
          expectedDoubleTimeHoursPerPeriod: parseFloat(values.expectedDoubleTimeHoursPerPeriod) || 0,
          effectiveDate: values.effectiveDate ? new Date(values.effectiveDate) : new Date(),
          taxWithholding: {
            federalTax: parseFloat(values.federalTax) || 0,
            stateTax: parseFloat(values.stateTax) || 0,
            socialSecurity: parseFloat(values.socialSecurity) || 0,
            medicare: parseFloat(values.medicare) || 0
          },
          deductions: {
            healthInsurance: parseFloat(values.healthInsurance) || 0,
            dentalInsurance: parseFloat(values.dentalInsurance) || 0,
            visionInsurance: parseFloat(values.visionInsurance) || 0,
            retirement401k: parseFloat(values.retirement401k) || 0
          }
        }
      };

      const res = await apiPut(`${apiPath.accountantUpdateEmployeeWage}/${employeeId}`, payload);
      if (res?.data?.success) {
        notification.success('Wage details updated successfully');
        if (onClose) onClose();
      } else {
        notification.error(res?.data?.message || 'Failed to update wage details');
      }
    } catch (error) {
      console.error('Error saving wage data:', error);
      notification.error('Failed to update wage details');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
        <div className="relative w-auto my-6 mx-auto max-w-3xl">
          <form className="sm:py-4 sm:px-2 py-8 px-7">
            <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full min-w-[900px] max-h-[96vh] overflow-y-auto bg-white outline-none focus:outline-none">
              <div className="flex items-center justify-between p-5 border-b border-solid border-slate-200 rounded-t">
                <h3 className="text-xl font-semibold">Employee Wage Setup</h3>
                <button
                  className="ml-auto flex items-center justify-center text-3xl text-black border-2 rounded-full h-8 w-8 float-right leading-none font-extralight outline-none focus:outline-none"
                  onClick={onClose}
                >
                  <span className="text-[#B8BBBF] text-4xl" title="Close">×</span>
                </button>
              </div>

              <div className="relative p-6 flex-auto">
                {employeeName && (
                  <h3 className="text-xl font-semibold mb-4">Wage Setup for: {employeeName}</h3>
                )}

                {/* ═══ SECTION 1: Compensation ═══ */}
                <div className="mb-6">
                  <h4 className="font-semibold text-md mb-3 border-b pb-2">Compensation</h4>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pay Type
                      </label>
                      <select
                        {...register('payType')}
                        className="block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0"
                      >
                        <option key="hourly" value="hourly">Hourly</option>
                        <option key="salary" value="salary">Salary</option>
                        <option key="contractor" value="contractor">Contractor</option>
                      </select>
                    </div>
                    <div className='pt-4'>
                      <OInputField
                        inputLabel="Effective Date"
                        type="date"
                        name="effectiveDate"
                        register={register('effectiveDate')}
                        errors={errors}
                      />
                    </div>
                  </div>

                  {(payType === 'hourly' || payType === 'contractor') && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <OInputField
                          inputLabel="Hourly Rate ($)"
                          type="text"
                          name="hourlyRate"
                          register={register('hourlyRate', {
                            required: 'Hourly rate is required',
                            validate: (value) => {
                              if (value === '' || value === '-') return true;
                              const num = parseFloat(value);
                              return (!isNaN(num) && num >= 0) || 'Hourly rate cannot be negative';
                            }
                          })}
                          errors={errors}
                          onChange={(e) => handleNumberInput('hourlyRate', e.target.value)}
                        />
                      </div>

                      {payType === 'hourly' && (
                        <>
                          <div>
                            <OInputField
                              inputLabel="Overtime Multiplier"
                              type="text"
                              name="overtimeMultiplier"
                              register={register('overtimeMultiplier', {
                                required: 'Overtime multiplier is required',
                                validate: (value) => {
                                  if (value === '' || value === '-') return true;
                                  const num = parseFloat(value);
                                  return (!isNaN(num) && num >= 0) || 'Overtime multiplier cannot be negative';
                                }
                              })}
                              errors={errors}
                              onChange={(e) => handleNumberInput('overtimeMultiplier', e.target.value)}
                            />
                          </div>
                          <div>
                            <OInputField
                              inputLabel="Double Time Multiplier"
                              type="text"
                              name="doubleTimeMultiplier"
                              register={register('doubleTimeMultiplier', {
                                validate: (value) => {
                                  if (value === '' || value === '-') return true;
                                  const num = parseFloat(value);
                                  return (!isNaN(num) && num >= 0) || 'Double time multiplier cannot be negative';
                                }
                              })}
                              errors={errors}
                              onChange={(e) => handleNumberInput('doubleTimeMultiplier', e.target.value)}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {payType === 'salary' && (
                    <div className="grid grid-cols-2 gap-4 mt-3">
                      <div>
                        <OInputField
                          inputLabel="Annual Salary ($)"
                          type="text"
                          name="annualSalary"
                          register={register('annualSalary', {
                            required: 'Annual salary is required',
                            validate: (value) => {
                              if (value === '' || value === '-') return true;
                              const num = parseFloat(value);
                              return (!isNaN(num) && num >= 0) || 'Annual salary cannot be negative';
                            }
                          })}
                          errors={errors}
                          onChange={(e) => handleNumberInput('annualSalary', e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* ═══ SECTION 2: Work Schedule ═══ */}
                <div className="mb-6">
                  <h4 className="font-semibold text-md mb-3 border-b pb-2">Work Schedule</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Pay Frequency
                      </label>
                      <select
                        {...register('payFrequency')}
                        className="block py-4 px-3 w-full text-sm text-gray-900 bg-transparent border-2 rounded-lg border-[#DFDFDF] focus:outline-none focus:ring-0"
                      >
                        <option key="weekly" value="weekly">Weekly</option>
                        <option key="bi-weekly" value="bi-weekly">Bi-Weekly</option>
                        <option key="semi-monthly" value="semi-monthly">Semi-Monthly</option>
                        <option key="monthly" value="monthly">Monthly</option>
                      </select>
                    </div>
                    <div className="pt-4">
                      <OInputField
                        inputLabel="Working Days Per Week"
                        type="text"
                        name="workingDaysPerWeek"
                        register={register('workingDaysPerWeek', {
                          required: 'Working days per week is required',
                          validate: (value) => {
                            if (value === '' || value === '-') return true;
                            const num = parseFloat(value);
                            return ((!isNaN(num) && num >= 0) && num <= 7) || 'Working days must be between 0 and 7';
                          }
                        })}
                        errors={errors}
                        onChange={(e) => handleNumberInput('workingDaysPerWeek', e.target.value)}
                      />
                    </div>
                    <div>
                      <OInputField
                        inputLabel="Regular Hours Per Day"
                        type="text"
                        name="regularHoursPerDay"
                        register={register('regularHoursPerDay', {
                          required: 'Regular hours per day is required',
                          validate: (value) => {
                            if (value === '' || value === '-') return true;
                            const num = parseFloat(value);
                            return (!isNaN(num) && num >= 0) || 'Regular hours cannot be negative';
                          }
                        })}
                        errors={errors}
                        onChange={(e) => handleNumberInput('regularHoursPerDay', e.target.value)}
                      />
                    </div>
                    {payType === 'hourly' && (
                      <>
                        <div>
                          <OInputField
                            inputLabel="Expected Overtime Hours Per Period"
                            type="text"
                            name="expectedOvertimeHoursPerPeriod"
                            register={register('expectedOvertimeHoursPerPeriod', {
                              required: 'Expected overtime hours is required',
                              validate: (value) => {
                                if (value === '' || value === '-') return true;
                                const num = parseFloat(value);
                                return (!isNaN(num) && num >= 0) || 'Expected overtime hours cannot be negative';
                              }
                            })}
                            errors={errors}
                            onChange={(e) => handleNumberInput('expectedOvertimeHoursPerPeriod', e.target.value)}
                          />
                        </div>
                        <div>
                          <OInputField
                            inputLabel="Expected Double Time Hours Per Period"
                            type="text"
                            name="expectedDoubleTimeHoursPerPeriod"
                            register={register('expectedDoubleTimeHoursPerPeriod', {
                              validate: (value) => {
                                if (value === '' || value === '-') return true;
                                const num = parseFloat(value);
                                return (!isNaN(num) && num >= 0) || 'Expected double time hours cannot be negative';
                              }
                            })}
                            errors={errors}
                            onChange={(e) => handleNumberInput('expectedDoubleTimeHoursPerPeriod', e.target.value)}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* ═══ SECTION 3: Taxes ═══ */}
                {payType !== 'contractor' && (
                  <div className="mb-6">
                    <h4 className="font-semibold text-md mb-3 border-b pb-2">Tax Withholding</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <OInputField
                          inputLabel="Federal Tax (%)"
                          type="text"
                          name="federalTax"
                          register={register('federalTax', {
                            required: 'Federal tax is required',
                            validate: (value) => {
                              if (value === '' || value === '-') return true;
                              const num = parseFloat(value);
                              return ((!isNaN(num) && num >= 0) && num <= 100) || 'Federal tax must be between 0 and 100';
                            }
                          })}
                          errors={errors}
                          onChange={(e) => handleNumberInput('federalTax', e.target.value)}
                        />
                      </div>
                      <div>
                        <OInputField
                          inputLabel="State Tax (%)"
                          type="text"
                          name="stateTax"
                          register={register('stateTax', {
                            required: 'State tax is required',
                            validate: (value) => {
                              if (value === '' || value === '-') return true;
                              const num = parseFloat(value);
                              return ((!isNaN(num) && num >= 0) && num <= 100) || 'State tax must be between 0 and 100';
                            }
                          })}
                          errors={errors}
                          onChange={(e) => handleNumberInput('stateTax', e.target.value)}
                        />
                      </div>
                      <div>
                        <OInputField
                          inputLabel="Social Security (%)"
                          type="text"
                          name="socialSecurity"
                          register={register('socialSecurity', {
                            required: 'Social Security is required',
                            validate: (value) => {
                              if (value === '' || value === '-') return true;
                              const num = parseFloat(value);
                              return ((!isNaN(num) && num >= 0) && num <= 100) || 'Social Security must be between 0 and 100';
                            }
                          })}
                          errors={errors}
                          onChange={(e) => handleNumberInput('socialSecurity', e.target.value)}
                        />
                      </div>
                      <div>
                        <OInputField
                          inputLabel="Medicare (%)"
                          type="text"
                          name="medicare"
                          register={register('medicare', {
                            required: 'Medicare is required',
                            validate: (value) => {
                              if (value === '' || value === '-') return true;
                              const num = parseFloat(value);
                              return ((!isNaN(num) && num >= 0) && num <= 100) || 'Medicare must be between 0 and 100';
                            }
                          })}
                          errors={errors}
                          onChange={(e) => handleNumberInput('medicare', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* ═══ SECTION 4: Benefits & Deductions ═══ */}
                {payType !== 'contractor' && (
                  <div className="mb-6">
                    <h4 className="font-semibold text-md mb-3 border-b pb-2">Benefits & Deductions</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <OInputField
                          inputLabel="Health Insurance ($)"
                          type="text"
                          name="healthInsurance"
                          register={register('healthInsurance', {
                            validate: (value) => {
                              if (value === '' || value === '-') return true;
                              const num = parseFloat(value);
                              return (!isNaN(num) && num >= 0) || 'Health Insurance cannot be negative';
                            }
                          })}
                          errors={errors}
                          onChange={(e) => handleNumberInput('healthInsurance', e.target.value)}
                        />
                      </div>
                      <div>
                        <OInputField
                          inputLabel="Dental Insurance ($)"
                          type="text"
                          name="dentalInsurance"
                          register={register('dentalInsurance', {
                            validate: (value) => {
                              if (value === '' || value === '-') return true;
                              const num = parseFloat(value);
                              return (!isNaN(num) && num >= 0) || 'Dental Insurance cannot be negative';
                            }
                          })}
                          errors={errors}
                          onChange={(e) => handleNumberInput('dentalInsurance', e.target.value)}
                        />
                      </div>
                      <div>
                        <OInputField
                          inputLabel="Vision Insurance ($)"
                          type="text"
                          name="visionInsurance"
                          register={register('visionInsurance', {
                            validate: (value) => {
                              if (value === '' || value === '-') return true;
                              const num = parseFloat(value);
                              return (!isNaN(num) && num >= 0) || 'Vision Insurance cannot be negative';
                            }
                          })}
                          errors={errors}
                          onChange={(e) => handleNumberInput('visionInsurance', e.target.value)}
                        />
                      </div>
                      <div>
                        <OInputField
                          inputLabel="401(k) Retirement ($)"
                          type="text"
                          name="retirement401k"
                          register={register('retirement401k', {
                            validate: (value) => {
                              if (value === '' || value === '-') return true;
                              const num = parseFloat(value);
                              return (!isNaN(num) && num >= 0) || '401(k) cannot be negative';
                            }
                          })}
                          errors={errors}
                          onChange={(e) => handleNumberInput('retirement401k', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* ═══ SECTION 5: Payroll Preview ═══ */}
                <div className="mt-6 border-t pt-4">
                  <h4 className="font-semibold text-lg mb-3">Payroll Preview</h4>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm text-gray-600">Regular Pay</p>
                      <p className="text-lg font-semibold text-blue-600">
                        ${regularPay.toFixed(2)}
                      </p>
                    </div>
                    <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                      <p className="text-sm text-gray-600">Overtime Pay</p>
                      <p className="text-lg font-semibold text-orange-600">
                        ${overtimePay.toFixed(2)}
                      </p>
                    </div>
                    <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                      <p className="text-sm text-gray-600">Double Time Pay</p>
                      <p className="text-lg font-semibold text-purple-600">
                        ${doubleTimePay.toFixed(2)}
                      </p>
                    </div>
                    {payType !== 'contractor' && (
                      <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                        <p className="text-sm text-gray-600">Estimated Taxes</p>
                        <p className="text-lg font-semibold text-red-600">
                          ${estimatedTaxes.toFixed(2)}
                        </p>
                      </div>
                    )}
                    {payType !== 'contractor' && (
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-sm text-gray-600">Deductions</p>
                        <p className="text-lg font-semibold text-gray-600">
                          ${totalDeductions.toFixed(2)}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm text-gray-600">Gross Pay</p>
                        <p className="text-2xl font-bold text-gray-800">${grossPay.toFixed(2)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Estimated Net Pay</p>
                        <p className="text-3xl font-bold text-green-600">${netPay.toFixed(2)}</p>
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      <p>Based on: {payType === 'hourly' ? `${regularHoursPerDay || 0} regular hours/day × ${workingDaysPerWeek || 0} days/week × ${payFrequency} period + ${expectedOvertimeHoursPerPeriod || 0} overtime hours + ${expectedDoubleTimeHoursPerPeriod || 0} double time hours` : payType === 'salary' ? `Annual salary of $${annualSalary} divided by ${payFrequency} period` : 'Contractor hourly rate'}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-solid border-slate-200 rounded-b flex items-center justify-center p-6">
                <button
                  onClick={onClose}
                  className="text-black bg-[#E1E1E1] font-normal px-12 py-2.5 text-sm outline-none focus:outline-none rounded mr-6 ease-linear transition-all duration-150"
                  type="button"
                >
                  Cancel
                </button>
                <button
                  className="bg-LightBlue text-white active:bg-emerald-600 font-normal text-sm px-8 py-2.5 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
                  onClick={handleSave}
                  disabled={loading}
                  type="button"
                >
                  {loading ? 'Saving...' : 'Save Wage Details'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      <div className="opacity-25 fixed inset-0 z-40 bg-black" />
    </>
  );
};

export default EmployeeWageSetup;